/**
 * The one AIRA conversation of the signed-in shopper — read by the Style Space
 * panel and by `/aira` alike. Optimistic user line, one request per message,
 * idempotent retry by `clientMessageId`, `degraded` never a crash.
 *
 * Transports: REST (`POST /aira/message`) by default; the socket when `/aira`
 * opened one (`connectSocket`) — the same state machine either way.
 */

import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useTokenStore } from '@/features/auth/store'
import { AIRA_PENDING_RETRY_MS, AIRA_UI_TIMEOUT_MS, AiraChatSocket, airaDeliveryOf, airaSocketTurnToResponse, airaSocketUrl, getAiraThread, mergeAiraMessages, newAiraClientMessageId, postAiraAction, postAiraMessage, sortAiraMessages, toAiraChatMessage, type AiraActionBody, type AiraActionRequest, type AiraChatMessage, type AiraContextEnvelope, type AiraLocalReason, type AiraSendMessageBody, type AiraSendMessageResponse, type AiraSocketEvent, type AiraSocketStatus } from '@/features/aira/api'
import { normalizeError } from '@/queries/core/errors'

export type AiraThreadLoadState = 'idle' | 'loading' | 'ready' | 'error'

export interface AiraSendInput {
  text: string
  envelope: AiraContextEnvelope
  lang: string
}

/** A Shuffle / Replace button: the line's text is the label the shopper saw; `action` is what goes out. */
export interface AiraActionInput extends AiraSendInput {
  action: AiraActionRequest
}

/** A `pending` answer means the first call for this id is still running; ask again, bounded. */
const MAX_PENDING_REASKS = Math.ceil(AIRA_UI_TIMEOUT_MS / AIRA_PENDING_RETRY_MS)

export const useAiraThreadStore = defineStore('aira-thread', () => {
  const tokenStore = useTokenStore()

  const messages = ref<AiraChatMessage[]>([])
  const threadId = ref<number | null>(null)
  const nextCursor = ref<string | null>(null)
  const loadState = ref<AiraThreadLoadState>('idle')
  const loadingOlder = ref(false)
  const inFlight = ref(new Set<string>())
  const typingIds = ref(new Set<string>())
  const socketStatus = ref<AiraSocketStatus>('closed')

  const timeoutTimers = new Map<string, ReturnType<typeof setTimeout>>()
  const pendingTimers = new Map<string, ReturnType<typeof setTimeout>>()
  const pendingReasks = new Map<string, number>()
  let loadPromise: Promise<void> | null = null
  let socket: AiraChatSocket | null = null
  let socketUnsubscribers: Array<() => void> = []

  const isTyping = computed(() => inFlight.value.size > 0 || typingIds.value.size > 0)
  const hasOlder = computed(() => nextCursor.value !== null)
  const isEmpty = computed(() => loadState.value === 'ready' && messages.value.length === 0)
  const usesSocket = computed(() => socketStatus.value === 'open')

  function findByKey(key: string): AiraChatMessage | undefined {
    return messages.value.find((message) => message.key === key)
  }

  function userLine(clientMessageId: string): AiraChatMessage | undefined {
    return findByKey(`u:${clientMessageId}`)
  }

  function upsert(message: AiraChatMessage): void {
    const index = messages.value.findIndex((entry) => entry.key === message.key)
    if (index >= 0) {
      const next = [...messages.value]
      next[index] = message
      messages.value = next
      return
    }
    messages.value = sortAiraMessages([...messages.value, message])
  }

  function remove(key: string): void {
    messages.value = messages.value.filter((message) => message.key !== key)
  }

  /* ——— history ——— */

  async function reload(): Promise<void> {
    if (loadState.value !== 'ready') loadState.value = 'loading'
    try {
      const page = (await getAiraThread()).data
      threadId.value = page.threadId
      if (loadState.value !== 'ready') nextCursor.value = page.nextCursor
      messages.value = mergeAiraMessages(messages.value, page.messages)
      loadState.value = 'ready'
    } catch {
      loadState.value = messages.value.length ? 'ready' : 'error'
    }
  }

  /** The first page, once per session; concurrent callers share the request. */
  function ensureLoaded(): Promise<void> {
    if (loadState.value === 'ready') return Promise.resolve()
    if (!loadPromise) {
      loadPromise = reload().finally(() => {
        loadPromise = null
      })
    }
    return loadPromise
  }

  async function loadOlder(): Promise<void> {
    if (!nextCursor.value || loadingOlder.value) return
    loadingOlder.value = true
    try {
      const page = (await getAiraThread({ cursor: nextCursor.value })).data
      nextCursor.value = page.nextCursor
      messages.value = mergeAiraMessages(messages.value, page.messages)
    } catch {
      // the log keeps what it has; the next scroll asks again
    } finally {
      loadingOlder.value = false
    }
  }

  /* ——— sending ——— */

  function clearTimers(clientMessageId: string): void {
    const timeout = timeoutTimers.get(clientMessageId)
    if (timeout) clearTimeout(timeout)
    timeoutTimers.delete(clientMessageId)
    const pending = pendingTimers.get(clientMessageId)
    if (pending) clearTimeout(pending)
    pendingTimers.delete(clientMessageId)
  }

  function settle(clientMessageId: string): void {
    clearTimers(clientMessageId)
    inFlight.value.delete(clientMessageId)
    typingIds.value.delete(clientMessageId)
  }

  function markLocalDegraded(
    user: AiraChatMessage,
    reason: AiraLocalReason,
    retryable: boolean,
    retryAfter: number | null = null,
  ): void {
    const clientMessageId = user.clientMessageId ?? ''
    const delivery = retryable ? 'degraded' : 'failed'
    upsert({ ...user, delivery, retryable, localReason: reason, retryAfter })
    upsert({
      id: 0,
      threadId: threadId.value,
      role: 'aira',
      text: '',
      surface: user.surface,
      status: 'degraded',
      context: null,
      actions: [],
      clientMessageId,
      mlRequestId: null,
      lang: user.lang,
      createdAt: new Date().toISOString(),
      key: `a:${clientMessageId}`,
      delivery,
      retryable,
      local: true,
      localReason: reason,
      retryAfter,
    })
  }

  function applyResponse(response: AiraSendMessageResponse, user: AiraChatMessage): void {
    const clientMessageId = user.clientMessageId ?? ''
    if (response.threadId != null) threadId.value = response.threadId
    // the id is ours: the key and every retry hang off it, whatever the row echoes
    // the wire row has no `action`: the retry of a Shuffle / Replace line must still post the action
    const stored = response.message.id
      ? { ...toAiraChatMessage(response.message), clientMessageId, action: user.action }
      : { ...user }
    if (response.status === 'pending' || !response.assistantMessage) {
      upsert({ ...stored, key: user.key, delivery: 'sent', retryable: false, local: !response.message.id })
      schedulePendingReask(clientMessageId)
      return
    }
    settle(clientMessageId)
    pendingReasks.delete(clientMessageId)
    upsert({
      ...stored,
      key: user.key,
      delivery: airaDeliveryOf(response.status),
      retryable: response.retryable,
      local: false,
      localReason: undefined,
      retryAfter: null,
    })
    upsert({
      ...toAiraChatMessage(response.assistantMessage),
      clientMessageId,
      key: `a:${clientMessageId}`,
      retryable: response.retryable,
    })
  }

  function applyFailure(user: AiraChatMessage, error: unknown): void {
    const clientMessageId = user.clientMessageId ?? ''
    settle(clientMessageId)
    const normalized = normalizeError(error)
    const status = normalized.statusCode
    if (status === 429) {
      const details = normalized.details as { retryAfter?: number } | undefined
      markLocalDegraded(user, 'rate_limited', true, details?.retryAfter ?? null)
      return
    }
    if (status && status >= 400 && status < 500) {
      markLocalDegraded(user, 'rejected', false)
      return
    }
    markLocalDegraded(user, 'network', true)
  }

  function armTimeout(clientMessageId: string): void {
    const existing = timeoutTimers.get(clientMessageId)
    if (existing) clearTimeout(existing)
    timeoutTimers.set(
      clientMessageId,
      setTimeout(() => {
        timeoutTimers.delete(clientMessageId)
        if (!inFlight.value.has(clientMessageId)) return
        inFlight.value.delete(clientMessageId)
        typingIds.value.delete(clientMessageId)
        const user = userLine(clientMessageId)
        // a late answer for the same id still lands through applyResponse and replaces this
        if (user) markLocalDegraded(user, 'timeout', true)
      }, AIRA_UI_TIMEOUT_MS),
    )
  }

  function schedulePendingReask(clientMessageId: string): void {
    const count = (pendingReasks.get(clientMessageId) ?? 0) + 1
    pendingReasks.set(clientMessageId, count)
    if (count > MAX_PENDING_REASKS) {
      const user = userLine(clientMessageId)
      settle(clientMessageId)
      if (user) markLocalDegraded(user, 'timeout', true)
      return
    }
    pendingTimers.set(
      clientMessageId,
      setTimeout(() => {
        pendingTimers.delete(clientMessageId)
        const user = userLine(clientMessageId)
        if (user && !findByKey(`a:${clientMessageId}`)) dispatch(user)
      }, AIRA_PENDING_RETRY_MS),
    )
  }

  function dispatch(user: AiraChatMessage): void {
    const clientMessageId = user.clientMessageId ?? ''
    inFlight.value.add(clientMessageId)
    armTimeout(clientMessageId)
    const context = user.context ?? { surface: user.surface, canvasItems: [] }
    if (user.action) {
      // Shuffle / Replace: the generator, never the socket and never the model
      const body: AiraActionBody = { ...user.action, clientActionId: clientMessageId, lang: user.lang ?? undefined, context }
      void postAiraAction(body)
        .then((response) => applyResponse(response.data, user))
        .catch((error) => applyFailure(user, error))
      return
    }
    const body: AiraSendMessageBody = {
      text: user.text,
      clientMessageId,
      lang: user.lang ?? undefined,
      context,
    }
    if (socket?.isOpen && socket.send(body, user.lang ?? 'en')) return
    void postAiraMessage(body)
      .then((response) => applyResponse(response.data, user))
      .catch((error) => applyFailure(user, error))
  }

  /** Renders the user's line at once and returns its id; the answer lands under the same id. */
  function send(input: AiraSendInput): string {
    const clientMessageId = newAiraClientMessageId()
    const user: AiraChatMessage = {
      id: 0,
      threadId: threadId.value,
      role: 'user',
      text: input.text,
      surface: input.envelope.surface,
      status: 'ok',
      context: input.envelope,
      actions: [],
      clientMessageId,
      mlRequestId: null,
      lang: input.lang,
      createdAt: new Date().toISOString(),
      key: `u:${clientMessageId}`,
      delivery: 'sending',
      retryable: false,
      local: true,
    }
    upsert(user)
    dispatch(user)
    return clientMessageId
  }

  /** A Shuffle / Replace button: renders the shopper's line at once, posts the action, the drafts land under the same id. */
  function sendAction(input: AiraActionInput): string {
    const clientMessageId = newAiraClientMessageId()
    const user: AiraChatMessage = {
      id: 0,
      threadId: threadId.value,
      role: 'user',
      text: input.text,
      surface: input.envelope.surface,
      status: 'ok',
      context: input.envelope,
      actions: [],
      clientMessageId,
      mlRequestId: null,
      lang: input.lang,
      createdAt: new Date().toISOString(),
      key: `u:${clientMessageId}`,
      delivery: 'sending',
      retryable: false,
      local: true,
      action: input.action,
    }
    upsert(user)
    dispatch(user)
    return clientMessageId
  }

  /** The same id again: the backend replays a stored reply or calls the model once more after a degraded one. */
  function retry(clientMessageId: string): void {
    const user = userLine(clientMessageId)
    if (!user || inFlight.value.has(clientMessageId)) return
    const reply = findByKey(`a:${clientMessageId}`)
    if (reply?.local) remove(reply.key)
    upsert({ ...user, delivery: 'sending', localReason: undefined, retryAfter: null })
    dispatch({ ...user, delivery: 'sending' })
  }

  /* ——— socket ——— */

  function handleSocketEvent(event: AiraSocketEvent): void {
    if (event.type === 'error') {
      if (!event.clientMessageId) return
      const user = userLine(event.clientMessageId)
      if (!user) return
      settle(event.clientMessageId)
      if (event.code === 'rate_limited') markLocalDegraded(user, 'rate_limited', true, event.retryAfter ?? null)
      else if (event.code === 'internal') markLocalDegraded(user, 'network', true)
      else markLocalDegraded(user, 'rejected', false)
      return
    }
    const user = userLine(event.clientMessageId)
    if (!user) return
    if (event.type === 'ack') {
      if (user.delivery === 'sending') upsert({ ...user, delivery: 'sent' })
      return
    }
    if (event.type === 'typing') {
      typingIds.value.add(event.clientMessageId)
      return
    }
    applyResponse(airaSocketTurnToResponse(event, user), user)
  }

  function connectSocket(): void {
    if (socket || typeof window === 'undefined') return
    const created = new AiraChatSocket(() => airaSocketUrl(tokenStore.accessToken))
    socket = created
    socketUnsubscribers = [
      created.onEvent(handleSocketEvent),
      created.onStatus(({ status, reconnected }) => {
        socketStatus.value = status
        // a drop may have swallowed a reply — the thread is the truth
        if (status === 'open' && reconnected) void reload()
      }),
    ]
    created.connect().catch(() => undefined)
  }

  function disconnectSocket(): void {
    socketUnsubscribers.forEach((unsubscribe) => unsubscribe())
    socketUnsubscribers = []
    socket?.close()
    socket = null
    socketStatus.value = 'closed'
  }

  function reset(): void {
    for (const id of [...timeoutTimers.keys(), ...pendingTimers.keys()]) clearTimers(id)
    pendingReasks.clear()
    inFlight.value.clear()
    typingIds.value.clear()
    messages.value = []
    threadId.value = null
    nextCursor.value = null
    loadState.value = 'idle'
    loadPromise = null
  }

  watch(
    () => tokenStore.accessToken,
    (token, previous) => {
      if (!token && previous) {
        disconnectSocket()
        reset()
        return
      }
      if (token && previous && token !== previous && socket) {
        disconnectSocket()
        connectSocket()
      }
    },
  )

  return {
    messages,
    threadId,
    nextCursor,
    loadState,
    loadingOlder,
    socketStatus,
    isTyping,
    hasOlder,
    isEmpty,
    usesSocket,
    ensureLoaded,
    reload,
    loadOlder,
    send,
    sendAction,
    retry,
    userLine,
    connectSocket,
    disconnectSocket,
    handleSocketEvent,
    reset,
  }
})
